import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
    Users,
    Plus,
    Trash2,
    MoreVertical,
    Mail,
    Shield,
    ShieldCheck,
    Loader2,
    Clock,
    CheckCircle
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';

const PERMISSIONS = [
    { id: 'create_posts', label: 'Create Posts' },
    { id: 'edit_posts', label: 'Edit Posts' },
    { id: 'delete_posts', label: 'Delete Posts' },
    { id: 'publish_posts', label: 'Publish Posts' },
    { id: 'manage_media', label: 'Manage Media' },
    { id: 'view_analytics', label: 'View Analytics' },
];

export default function Team() {
    const { api, isAdmin } = useAuth();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [memberToDelete, setMemberToDelete] = useState(null);
    const [inviting, setInviting] = useState(false);

    const [inviteForm, setInviteForm] = useState({
        email: '',
        role: 'member',
        permissions: []
    });

    const fetchMembers = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/team/members');
            setMembers(response.data);
        } catch (error) {
            console.error('Failed to fetch members:', error);
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);

    const handleInvite = async () => {
        if (!inviteForm.email) {
            toast.error('Please enter an email address');
            return;
        }

        setInviting(true);
        try {
            await api.post('/team/invite', inviteForm);
            toast.success('Invitation sent!');
            setInviteDialogOpen(false);
            setInviteForm({ email: '', role: 'member', permissions: [] });
            fetchMembers();
        } catch (error) {
            console.error('Failed to invite:', error);
            toast.error(error.response?.data?.detail || 'Failed to send invitation');
        } finally {
            setInviting(false);
        }
    };

    const handleRemove = async () => {
        if (!memberToDelete) return;
        try {
            await api.delete(`/team/members/${memberToDelete.id}`);
            toast.success('Team member removed');
            setMembers(prev => prev.filter(m => m.id !== memberToDelete.id));
            setDeleteDialogOpen(false);
            setMemberToDelete(null);
        } catch (error) {
            console.error('Failed to remove member:', error);
            toast.error('Failed to remove team member');
        }
    };

    const togglePermission = (permissionId) => {
        setInviteForm(prev => ({
            ...prev,
            permissions: prev.permissions.includes(permissionId)
                ? prev.permissions.filter(p => p !== permissionId)
                : [...prev.permissions, permissionId]
        }));
    };

    const getInitials = (name) => {
        return name
            ?.split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2) || 'U';
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="team-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold">Team</h1>
                    <p className="text-muted-foreground mt-1">
                        Manage team members and permissions
                    </p>
                </div>
                {isAdmin && (
                    <Button
                        className="gradient-bg"
                        onClick={() => setInviteDialogOpen(true)}
                        data-testid="invite-member-btn"
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Invite Member
                    </Button>
                )}
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-[400px]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            ) : members.length === 0 ? (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
                        <Users className="h-16 w-16 mb-4 opacity-50" />
                        <p className="text-lg font-medium">No team members yet</p>
                        <p className="text-sm">Invite your team to collaborate on social media</p>
                        {isAdmin && (
                            <Button
                                variant="outline"
                                className="mt-4"
                                onClick={() => setInviteDialogOpen(true)}
                            >
                                <Plus className="mr-2 h-4 w-4" />
                                Invite Your First Member
                            </Button>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Users className="h-5 w-5" />
                            Team Members ({members.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {members.map(member => (
                                <div
                                    key={member.id}
                                    className="flex items-center justify-between p-4 rounded-lg bg-muted/50"
                                    data-testid={`team-member-${member.id}`}
                                >
                                    <div className="flex items-center gap-4">
                                        <Avatar className="h-12 w-12">
                                            <AvatarFallback className="bg-primary text-primary-foreground">
                                                {getInitials(member.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium">{member.name}</p>
                                                {member.role === 'admin' ? (
                                                    <Badge className="bg-fuchsia-500/20 text-fuchsia-500 border-fuchsia-500/30">
                                                        <ShieldCheck className="mr-1 h-3 w-3" />
                                                        Admin
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="secondary">
                                                        <Shield className="mr-1 h-3 w-3" />
                                                        Member
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-sm text-muted-foreground">{member.email}</p>
                                            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                                {member.status === 'active' ? (
                                                    <>
                                                        <CheckCircle className="h-3 w-3 text-green-500" />
                                                        <span>Active</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Clock className="h-3 w-3 text-yellow-500" />
                                                        <span>Pending</span>
                                                    </>
                                                )}
                                                <span>•</span>
                                                <span>Invited {format(new Date(member.invited_at), 'MMM d, yyyy')}</span>
                                            </div>
                                        </div>
                                    </div>
                                    {isAdmin && (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon">
                                                    <MoreVertical className="h-4 w-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem
                                                    className="text-destructive focus:text-destructive"
                                                    onClick={() => {
                                                        setMemberToDelete(member);
                                                        setDeleteDialogOpen(true);
                                                    }}
                                                >
                                                    <Trash2 className="mr-2 h-4 w-4" />
                                                    Remove
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Invite Team Member</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email Address</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="colleague@company.com"
                                value={inviteForm.email}
                                onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                                data-testid="invite-email-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Role</Label>
                            <Select
                                value={inviteForm.role}
                                onValueChange={(value) => setInviteForm(prev => ({ ...prev, role: value }))}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select role" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="member">Member</SelectItem>
                                    <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Permissions</Label>
                            <div className="grid grid-cols-2 gap-2">
                                {PERMISSIONS.map(perm => (
                                    <Button
                                        key={perm.id}
                                        variant="outline"
                                        size="sm"
                                        className={cn(
                                            "justify-start",
                                            inviteForm.permissions.includes(perm.id) && "bg-primary/10 border-primary"
                                        )}
                                        onClick={() => togglePermission(perm.id)}
                                    >
                                        <div className={cn(
                                            "w-4 h-4 rounded border mr-2 flex items-center justify-center",
                                            inviteForm.permissions.includes(perm.id) && "bg-primary border-primary"
                                        )}>
                                            {inviteForm.permissions.includes(perm.id) && (
                                                <CheckCircle className="h-3 w-3 text-primary-foreground" />
                                            )}
                                        </div>
                                        {perm.label}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleInvite} disabled={inviting}>
                            {inviting ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <Mail className="mr-2 h-4 w-4" />
                            )}
                            Send Invite
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Remove Team Member</DialogTitle>
                    </DialogHeader>
                    <p>Are you sure you want to remove {memberToDelete?.name} from the team?</p>
                    <p className="text-sm text-muted-foreground">
                        They will lose access to all shared resources and scheduled posts.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleRemove}>
                            Remove
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
